import {Box, Typography, Chip, IconButton} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {ImportJob} from './types';
import {useEffect, useState} from 'react';
import {Button, Modal} from "@origamirisk/origami-storybook";
import {useAccount} from '../../context/AccountContext';
import {formatVersionCountLabel} from './versionCountLabel';

interface ImportJobCardProps {
    job: ImportJob;
    onCancel?: (jobId: string) => void;
    policySetMap?: Record<string, string>;
}

// Helper to format processing time as mm:ss
function formatProcessingTime(startedTs?: string | null): string {
    if (!startedTs) return '-';

    const start = new Date(startedTs).getTime();
    const now = Date.now();
    const durationMs = now - start;

    if (durationMs < 0) return '-';

    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// Helper to format duration between two timestamps as mm:ss
function formatDuration(startedTs?: string | null, completedTs?: string | null): string {
    if (!startedTs || !completedTs) return '-';

    const start = new Date(startedTs).getTime();
    const end = new Date(completedTs).getTime();
    const durationMs = end - start;

    if (durationMs < 0) return '-';

    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// Helper to get import components summary
function getImportSummary(job: ImportJob): string {
    const components: string[] = [];
    if (job.publishRequest.importMetadata) components.push('Metadata');
    if (job.publishRequest.importCommonTables) components.push('Common Tables');
    if (job.publishRequest.importGlobalScripts) components.push('Global Scripts');
    if (job.publishRequest.importRateTables) components.push('Rate Tables');
    if (job.publishRequest.importAlgo) components.push('Algo');
    if (job.publishRequest.importValidations) components.push('Validations');
    return components.join(', ');
}

// Helper to format time ago
function formatTimeAgo(timestamp: string): string {
    const now = Date.now();
    const then = new Date(timestamp).getTime();
    const diffMs = now - then;
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
}

function getPolicyLine(job: ImportJob, policySetMap?: Record<string, string>): string {
    const ps =
        job.publishRequest.policySet ||
        policySetMap?.[String(job.publishRequest.policySetId)] ||
        `PS:${job.publishRequest.policySetId}`;
    return `${job.publishRequest.state} • ${ps}`;
}

export function ImportJobCard({job, onCancel, policySetMap}: ImportJobCardProps) {
    const {isAdminUser} = useAccount();
    const [, forceUpdate] = useState(0);
    const [errorDetailsOpen, setErrorDetailsOpen] = useState(false);

    // Update timer every second for IN_PROGRESS jobs
    useEffect(() => {
        if (job.status === 'IN_PROGRESS') {
            const interval = setInterval(() => {
                forceUpdate(n => n + 1);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [job.status]);

    const isActive = job.status === 'QUEUED' || job.status === 'IN_PROGRESS';
    const isFailed = job.status === 'FAILED';
    const isCompleted = job.status === 'COMPLETED';

    const versionCountLabel = formatVersionCountLabel(job);
    const importSummary = getImportSummary(job);

    const handleCancel = () => {
        if (onCancel) {
            onCancel(job.jobId);
        }
    };

    // Active job card (QUEUED or IN_PROGRESS)
    if (isActive) {
        const progress = job.progressPercentage || 0;
        const isInProgress = job.status === 'IN_PROGRESS';
        const timer = isInProgress ? formatProcessingTime(job.startedTs) : null;

        return (
            <Box
                sx={{
                    padding: '5px 10px',
                    mb: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    borderLeft: '3px solid',
                    borderRadius: '0 5px 5px 0',
                    borderLeftColor: isInProgress ? '#43a047' : '#455A64',
                    backgroundColor: isInProgress ? '#f5f5f5' : '#f5f5f5',
                }}
            >
                {/* Progress background fill */}
                {isInProgress && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: `${progress}%`,
                            backgroundColor: '#c8e6c9',
                            transition: 'width 0.3s ease-in-out',
                            zIndex: 0
                        }}
                    />
                )}

                <Box sx={{position: 'relative', zIndex: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            {isInProgress ? (
                                <Typography variant="subtitle1" sx={{
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    position: 'relative',
                                    top: '01px',
                                    fontFamily: 'monospace',
                                    color: '#2e7d32',
                                    minWidth: '20px'
                                }}>
                                    {progress}%
                                </Typography>
                            ) : (
                                <ScheduleIcon sx={{fontSize: 16, color: '#9e9e9e'}}/>
                            )}
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                {getPolicyLine(job, policySetMap)}
                            </Typography>
                            {job.status === 'QUEUED' && (
                                <Chip label="Queued" size="small" sx={{
                                    fontSize: '10px',
                                    height: '20px',
                                    borderRadius: '2px',
                                    fontFamily: 'monospace'
                                }}/>
                            )}
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                            {timer && (
                                <Typography sx={{fontWeight: 600, fontSize: '11px', fontFamily: 'monospace'}}>
                                    {timer}
                                </Typography>
                            )}
                            {onCancel && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    variant="contained"
                                    onClick={handleCancel}
                                    style={{
                                        height: '20px',
                                    }}
                                >
                                    Cancel
                                </Button>
                            )}

                        </Box>
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                        <Typography variant="body2" color="text.secondary" style={{fontSize: '11px'}}>
                            {versionCountLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" style={{fontSize: '11px'}}>
                            •
                        </Typography>
                        <Typography variant="body2" color="text.secondary" style={{fontSize: '11px'}}>
                            {importSummary}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    // Failed job card
    if (isFailed) {
        const hasErrorDetails = Boolean(
            (job.errorMessage && job.errorMessage.trim()) || job.errorStackTrace
        );
        const inlineErrorText =
            (job.errorMessage && job.errorMessage.trim()) ||
            (job.errorStackTrace ? 'Stack trace available — use the info button to view.' : '');
        const errorModalTitle = (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                    <span>Import failed</span>
                    <Chip label="FAILED" size="small" color="error"
                          sx={{fontSize: '10px', height: '20px', borderRadius: '2px', fontFamily: 'monospace'}}/>
                </Box>
                <Typography variant="body2" sx={{fontWeight: 600, color: 'text.primary'}}>
                    {getPolicyLine(job, policySetMap)}
                </Typography>
                <Typography variant="caption" sx={{fontFamily: 'monospace', color: 'text.secondary'}}>
                    Job ID: {job.jobId}
                </Typography>
            </Box>
        );

        return (
            <>
                <Box
                    sx={{
                        borderLeft: '2px solid #E53935',
                        padding: '5px 10px',
                        mb: 2,
                        backgroundColor: '#FFEBEE'
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <ErrorIcon sx={{fontSize: 20, color: '#E53935'}}/>
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                {getPolicyLine(job, policySetMap)}
                            </Typography>
                            <Chip label="Failed" size="small" color="error"
                                  sx={{fontSize: '10px', height: '20px', borderRadius: '2px', fontFamily: 'monospace'}}/>
                        </Box>
                    </Box>

                    {hasErrorDetails &&
                        (isAdminUser ? (
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1}}>
                                <Typography
                                    variant="body2"
                                    color={job.errorMessage && job.errorMessage.trim() ? 'error' : 'text.secondary'}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontFamily: 'monospace',
                                        fontSize: '11px',
                                    }}
                                >
                                    {inlineErrorText}
                                </Typography>
                                <IconButton
                                    size="small"
                                    aria-label="View full error and stack trace"
                                    onClick={() => setErrorDetailsOpen(true)}
                                    sx={{flexShrink: 0, color: 'text.secondary'}}
                                >
                                    <InfoOutlinedIcon sx={{fontSize: 18}}/>
                                </IconButton>
                            </Box>
                        ) : (
                            job.errorMessage &&
                            job.errorMessage.trim() && (
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{mb: 1, fontFamily: 'monospace', fontSize: '11px'}}
                                >
                                    {job.errorMessage}
                                </Typography>
                            )
                        ))}

                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(job.updatedTs)}
                        </Typography>
                    </Box>
                </Box>

                {isAdminUser && (
                    <Modal
                        open={errorDetailsOpen}
                        onClose={() => setErrorDetailsOpen(false)}
                        size="md"
                        title={errorModalTitle as any}
                        actions={
                            <Button color="secondary" variant="outlined" onClick={() => setErrorDetailsOpen(false)}>
                                Close
                            </Button>
                        }
                        sx={{
                            '& .MuiDialog-paper': {
                                maxWidth: '640px',
                                width: '90vw',
                            },
                        }}
                    >
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '60vh', overflow: 'auto'}}>
                            {job.errorMessage && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{mb: 0.5}}>
                                        Message
                                    </Typography>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            backgroundColor: '#ffebee',
                                            borderLeft: '4px solid #E53935',
                                            fontFamily: 'monospace',
                                            fontSize: '12px',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {job.errorMessage}
                                    </Box>
                                </Box>
                            )}
                            {job.errorStackTrace ? (
                                <Box>
                                    <Typography variant="subtitle2" sx={{mb: 0.5}}>
                                        Stack trace
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            p: 1.5,
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '11px',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            overflow: 'auto',
                                            maxHeight: '40vh',
                                        }}
                                    >
                                        {job.errorStackTrace}
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                                    No stack trace was recorded for this import.
                                </Typography>
                            )}
                        </Box>
                    </Modal>
                )}
            </>
        );
    }

    // Completed job (compact single line)
    if (isCompleted) {
        const duration = formatDuration(job.startedTs, job.completedTs);

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1,
                    borderBottom: '1px solid #f0f0f0',
                    '&:last-child': {
                        borderBottom: 'none'
                    }
                }}
            >
                <CheckCircleIcon sx={{fontSize: 16, color: '#43a047'}}/>
                <Typography variant="body2" sx={{flex: 1}}>
                    {getPolicyLine(job, policySetMap)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {duration}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatTimeAgo(job.completedTs || job.updatedTs)}
                </Typography>
            </Box>
        );
    }

    // Other statuses (CANCELLED, DEAD_LETTERED)
    return null;
}
